import type { Reading, WantToRead } from '@bookclub/shared';

type ShelfReading = Pick<Reading, 'bookKey' | 'status' | 'startedAt' | 'finishedAt' | 'updatedAt'>;

/**
 * What stands on which shelf. Each book once, on the shelf it's on now: reading wins over saved, then read,
 * then stopped. Newest first on each; reading by when it started, so a book doesn't jump along the shelf as
 * you update it. Read books are grouped by the year you finished them.
 */
export function arrangeShelves<R extends ShelfReading, W extends Pick<WantToRead, 'bookKey'>>(readings: readonly R[], want: readonly W[]) {
  const seen = new Set<string>();
  const once = <T extends { bookKey: string }>(books: T[]) =>
    books.filter((b) => {
      if (seen.has(b.bookKey)) return false;
      seen.add(b.bookKey);
      return true;
    });
  const having = (status: Reading['status']) => readings.filter((r) => r.status === status);
  const reading = once(having('reading').sort((a, b) => b.startedAt.localeCompare(a.startedAt)));
  const wanted = once([...want]);
  const finished = once(having('finished').sort((a, b) => (b.finishedAt ?? '').localeCompare(a.finishedAt ?? '')));
  const stopped = once(having('stopped').sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)));
  const years = new Map<number | null, R[]>();
  for (const r of finished) {
    const year = r.finishedAt ? new Date(r.finishedAt).getFullYear() : null;
    years.set(year, [...(years.get(year) ?? []), r]);
  }
  return { reading, wanted, years: [...years.entries()], stopped };
}
